import React, { PureComponent } from "react";
import * as Icon from "react-feather";

import SearchableDropdown from "../common/searchable_dropdown/SearchableDropdown";

/**
 * Inline-editable cell for NATIVE engagement fields.
 *
 * Distinct from TrackerCell because the value source is the engagement row itself
 * (not a tracker_value record), and we PATCH the engagement on change.
 *
 * Supported types:
 *   - "text"        →  click-to-edit text input (used for engagement name)
 *   - "date"        →  shows formatted date, opens native picker via showPicker()
 *   - "checkbox"    →  centered checkbox (tax_estimate)
 *   - "flow_state"  →  colored pill dropdown of FlowStates filtered to row.flow_id
 *   - "flow"        →  pill dropdown of Flows (workflow type)
 *   - "team"        →  pill dropdown of Teams
 *
 * PureComponent: re-renders only when its props change (shallow compare).
 */
class NativeCell extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			editingText: false,
			localText: "",
			dropdownOpen: false,
		};
	}

	_readableTextColor(hex) {
		if (!hex || hex[0] !== "#" || hex.length < 7) return "#2b3340";
		const r = parseInt(hex.substr(1, 2), 16);
		const g = parseInt(hex.substr(3, 2), 16);
		const b = parseInt(hex.substr(5, 2), 16);
		const lum = (r * 299 + g * 587 + b * 114) / 1000;
		return lum > 160 ? "#2b3340" : "#ffffff";
	}

	_formatDateValue(iso) {
		if (!iso) return "";
		if (typeof iso === "string" && iso.length >= 10) return iso.substr(0, 10);
		try {
			const d = new Date(iso);
			if (isNaN(d.getTime())) return "";
			const y = d.getFullYear();
			const m = String(d.getMonth() + 1).padStart(2, "0");
			const day = String(d.getDate()).padStart(2, "0");
			return y + "-" + m + "-" + day;
		} catch (e) {
			return "";
		}
	}

	_formatDateDisplay(iso) {
		const v = this._formatDateValue(iso);
		if (!v) return null;
		const [y, m, d] = v.split("-");
		const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
		const mi = parseInt(m, 10) - 1;
		if (mi < 0 || mi > 11) return v;
		return months[mi] + " " + parseInt(d, 10) + ", " + y;
	}

	// ---------- Text ----------

	startTextEdit = () => {
		this.setState({ editingText: true, localText: this.props.value || "" }, () => {
			if (this._inputRef) this._inputRef.focus();
		});
	};

	onTextChange = e => {
		this.setState({ localText: e.target.value });
	};

	commitText = () => {
		const original = this.props.value || "";
		const next = this.state.localText;
		this.setState({ editingText: false });
		if (next !== original) {
			this.props.onChange(next);
		}
	};

	onTextKeyDown = e => {
		if (e.key === "Enter") {
			e.preventDefault();
			this.commitText();
		} else if (e.key === "Escape") {
			this.setState({ editingText: false, localText: this.props.value || "" });
		}
	};

	renderText() {
		const { value } = this.props;
		const { editingText, localText } = this.state;

		if (editingText) {
			return (
				<input
					ref={el => (this._inputRef = el)}
					className="et-cell__text-input"
					value={localText}
					onChange={this.onTextChange}
					onBlur={this.commitText}
					onKeyDown={this.onTextKeyDown}
					data-tracker-focus
				/>
			);
		}

		return (
			<button className="et-cell__text" onClick={this.startTextEdit} data-tracker-focus>
				{value ? <span>{value}</span> : <span className="et-cell__placeholder">—</span>}
			</button>
		);
	}

	// ---------- Date ----------

	onDateChange = e => {
		const v = e.target.value || "";
		this.props.onChange(v || null);
	};

	openDatePicker = e => {
		e.preventDefault();
		const input = this._dateInputRef;
		if (!input) return;
		// Modern browsers: showPicker() reliably opens the native calendar.
		if (typeof input.showPicker === "function") {
			try {
				input.showPicker();
				return;
			} catch (err) {
				// Some browsers throw if not user-activated — fall through.
			}
		}
		input.focus();
		input.click();
	};

	renderDate() {
		const display = this._formatDateDisplay(this.props.value);
		const value = this._formatDateValue(this.props.value);
		return (
			<button
				type="button"
				className="et-cell__date"
				onClick={this.openDatePicker}
				data-tracker-focus
			>
				<Icon.Calendar size={11} className="et-cell__date-icon" />
				<span className={display ? "et-cell__date-display" : "et-cell__date-display et-cell__placeholder"}>
					{display || "—"}
				</span>
				<input
					ref={el => (this._dateInputRef = el)}
					type="date"
					className="et-cell__date-input"
					value={value}
					onChange={this.onDateChange}
					tabIndex={-1}
				/>
			</button>
		);
	}

	// ---------- Checkbox ----------

	toggleChecked = () => {
		this.props.onChange(!this.props.value);
	};

	renderCheckbox() {
		const checked = !!this.props.value;
		return (
			<button
				className={"et-cell__check " + (checked ? "et-cell__check--on" : "")}
				onClick={this.toggleChecked}
				data-tracker-focus
			>
				{checked && <Icon.Check size={13} />}
			</button>
		);
	}

	// ---------- Generic pill dropdown (used by flow_state, flow, team) ----------

	toggleDropdown = () => {
		this.setState({ dropdownOpen: !this.state.dropdownOpen });
	};

	closeDropdown = () => {
		this.setState({ dropdownOpen: false });
	};

	pickOption = id => {
		this.setState({ dropdownOpen: false });
		this.props.onChange(id);
	};

	clearOption = e => {
		e.stopPropagation();
		this.setState({ dropdownOpen: false });
		this.props.onChange(null);
	};

	_renderPillDropdown({ selectedColor, selectedLabel, options, selectedId, emptyMessage }) {
		const { dropdownOpen } = this.state;
		const pillStyle = selectedLabel
			? {
					background: selectedColor || "#cfd6e0",
					color: this._readableTextColor(selectedColor || "#cfd6e0"),
			  }
			: undefined;

		return (
			<div className="et-cell__dropdown">
				<button
					className={"et-pill " + (selectedLabel ? "" : "et-pill--empty")}
					style={pillStyle}
					onClick={this.toggleDropdown}
					data-tracker-focus
				>
					<span className="et-pill__label">{selectedLabel || "—"}</span>
					<Icon.ChevronDown size={11} className="et-pill__chevron" />
				</button>

				{dropdownOpen && (
					<>
						<div className="et-cell__backdrop" onClick={this.closeDropdown} />
						<div className="et-cell__menu">
							{options.length === 0 && (
								<div className="et-cell__menu-empty">{emptyMessage || "No options"}</div>
							)}
							{options.map(o => (
								<button
									key={o.key}
									className={
										"et-cell__menu-item " +
										(o.key === selectedId ? "et-cell__menu-item--active" : "")
									}
									onClick={() => this.pickOption(o.key)}
								>
									<span
										className="et-cell__menu-swatch"
										style={{ background: o.color || "#cfd6e0" }}
									/>
									<span className="et-cell__menu-label">{o.label}</span>
									{o.key === selectedId && <Icon.Check size={12} className="et-cell__menu-check" />}
								</button>
							))}
							{selectedLabel && (
								<>
									<div className="et-cell__menu-divider" />
									<button
										className="et-cell__menu-item et-cell__menu-item--clear"
										onClick={this.clearOption}
									>
										<Icon.X size={12} className="et-cell__menu-swatch-clear" />
										<span>Clear</span>
									</button>
								</>
							)}
						</div>
					</>
				)}
			</div>
		);
	}

	renderFlowState() {
		const { row, flowStates } = this.props;
		const native = row.native || {};
		const selected = native.flow_state || null;
		const options = (flowStates || [])
			.filter(fs => (native.flow_id ? fs.flow_id === native.flow_id : true))
			.map(fs => ({ key: fs.id, label: fs.name, color: fs.color }));

		return this._renderPillDropdown({
			selectedColor: selected ? selected.color : null,
			selectedLabel: selected ? selected.name : null,
			options,
			selectedId: selected ? selected.id : null,
			emptyMessage: "No statuses available for this flow",
		});
	}

	renderFlow() {
		const { row, flows } = this.props;
		const native = row.native || {};
		const selectedId = native.flow_id || null;
		const options = (flows || []).map(f => ({ key: f.id, label: f.name, color: "#dfe3eb" }));
		const selected = options.find(o => o.key === selectedId);

		return this._renderPillDropdown({
			selectedColor: selected ? selected.color : null,
			selectedLabel: selected ? selected.label : null,
			options,
			selectedId,
			emptyMessage: "No workflows defined",
		});
	}

	renderTeam() {
		const { row, teams } = this.props;
		const native = row.native || {};
		const selectedId = native.team_id || null;
		const options = (teams || []).map(t => ({ key: t.id, label: t.name, color: "#c9d8f5" }));
		const selected = options.find(o => o.key === selectedId);

		return this._renderPillDropdown({
			selectedColor: selected ? selected.color : null,
			selectedLabel: selected ? selected.label : null,
			options,
			selectedId,
			emptyMessage: "No teams defined",
		});
	}

	// ---------- Client (searchable picker) ----------

	renderClient() {
		const { row } = this.props;
		const label = row ? row.client_name : null;
		const { dropdownOpen } = this.state;
		return (
			<div className="et-cell__dropdown">
				<button
					className={"et-pill " + (label ? "" : "et-pill--empty")}
					onClick={this.toggleDropdown}
					data-tracker-focus
				>
					<span className="et-pill__label">{label || "—"}</span>
					<Icon.ChevronDown size={11} className="et-pill__chevron" />
				</button>
				{dropdownOpen && (
					<>
						<div className="et-cell__backdrop" onClick={this.closeDropdown} />
						<div className="et-cell__menu et-cell__menu--client">
							<SearchableDropdown
								type="clients"
								alwaysShow
								selectedName={label || ""}
								onSelect={c => {
									this.setState({ dropdownOpen: false });
									// Pass id + name so the parent can update the row's client label
									// immediately (no full reload needed).
									this.props.onChange(c && c.id ? { id: c.id, name: c.name } : null);
								}}
							/>
						</div>
					</>
				)}
			</div>
		);
	}

	// ---------- Read-only rendering (type-aware) ----------

	_readonlyText(v) {
		return <span className="et-cell__readonly">{v == null || v === "" ? "—" : String(v)}</span>;
	}

	renderReadonly() {
		const { type, row, value, flows, teams } = this.props;
		const native = (row && row.native) || {};
		switch (type) {
			case "client":
				return this._readonlyText(row ? row.client_name : null);
			case "flow": {
				const f = (flows || []).find(x => x.id === native.flow_id);
				return this._readonlyText(f ? f.name : null);
			}
			case "team": {
				const t = (teams || []).find(x => x.id === native.team_id);
				return this._readonlyText(t ? t.name : null);
			}
			case "flow_state": {
				// Keep the colored status pill — just not clickable.
				const s = native.flow_state;
				if (!s) return this._readonlyText(null);
				const bg = s.color || "#cfd6e0";
				return (
					<span className="et-pill et-pill--readonly" style={{ background: bg, color: this._readableTextColor(bg) }}>
						<span className="et-pill__label">{s.name}</span>
					</span>
				);
			}
			case "date":
				return this._readonlyText(this._formatDateDisplay(value));
			case "checkbox":
				return this._readonlyText(value ? "Yes" : null);
			case "text":
			default:
				return this._readonlyText(value);
		}
	}

	render() {
		if (this.props.readonly) {
			return this.renderReadonly();
		}
		switch (this.props.type) {
			case "date":
				return this.renderDate();
			case "checkbox":
				return this.renderCheckbox();
			case "flow_state":
				return this.renderFlowState();
			case "flow":
				return this.renderFlow();
			case "team":
				return this.renderTeam();
			case "client":
				return this.renderClient();
			case "text":
			default:
				return this.renderText();
		}
	}
}

export default NativeCell;
